import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../context/AuthContext";
import { useRealtimeEvent } from "../context/RealtimeContext";
import { api, fmtDate, formatErr, fileUrl } from "../lib/api";
import { Empty, Field, PageHeader, StatusBadge, inputCls } from "./kit";

const TABS = ["all", "pending", "sent", "accepted", "declined", "rejected"];
const EDIT = [["name", "Full name"], ["email", "Email"], ["platform", "Platform"], ["game_version", "Game version"], ["service", "Service"],
  ["service_option", "Service option"], ["current_progress", "Current progress"], ["target_progress", "Target progress"], ["timeframe", "Preferred timeframe"]];

function Info({ q }) {
  const rows = [["Platform", q.platform], ["Game", q.game_version], ["Service", q.service], ["Option", q.service_option], ["Current", q.current_progress],
    ["Target", q.target_progress], ["Timeframe", q.timeframe]].filter((r) => r[1]);
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
      {rows.map(([k, v]) => <div key={k}><p className="text-xs text-slate-500 uppercase tracking-wider">{k}</p><p className="text-slate-200">{v}</p></div>)}
      {q.requirements && <div className="col-span-2"><p className="text-xs text-slate-500 uppercase tracking-wider">Requirements</p><p className="text-slate-200 whitespace-pre-wrap">{q.requirements}</p></div>}
      <CustomAnswers items={q.custom} />
      {q.screenshots?.length > 0 && (
        <div className="col-span-2 flex flex-wrap gap-2">{q.screenshots.map((s) => <a key={s.path} href={fileUrl(s.url)} target="_blank" rel="noreferrer"><img src={fileUrl(s.url)} alt="" className="w-24 h-16 object-cover rounded border border-[#007bff]/30" /></a>)}</div>
      )}
    </div>
  );
}

export function CustomAnswers({ items }) {
  if (!items?.length) return null;
  return (
    <div className="col-span-2 rounded-lg border border-[#007bff]/25 p-3 grid sm:grid-cols-2 gap-2" data-testid="custom-answers">
      <p className="sm:col-span-2 text-xs uppercase tracking-widest text-[#00d9ff]">Additional answers</p>
      {items.map((c) => <div key={c.key}><p className="text-xs text-slate-500 uppercase tracking-wider">{c.label}</p><p className="text-slate-200 text-sm whitespace-pre-wrap">{c.value}</p></div>)}
    </div>
  );
}

function Pricing({ q }) {
  return (
    <div className="rounded-xl bg-[#061232] border border-[#007bff]/30 p-4 text-sm space-y-1" data-testid="quote-pricing">
      <div className="flex justify-between"><span className="text-slate-400">Base price</span><span>{q.price} {q.currency}</span></div>
      {q.extra_charges > 0 && <div className="flex justify-between"><span className="text-slate-400">Additional charges</span><span>+{q.extra_charges}</span></div>}
      {q.discount > 0 && <div className="flex justify-between"><span className="text-slate-400">Discount</span><span>-{q.discount}</span></div>}
      <div className="flex justify-between font-display text-2xl pt-1"><span className="text-metal">Total</span><span className="text-electric">{q.total} {q.currency}</span></div>
      {q.eta && <p className="text-slate-300">Estimated completion: <b>{q.eta}</b></p>}
      {q.admin_notes && <p className="text-slate-300 whitespace-pre-wrap">Notes: {q.admin_notes}</p>}
    </div>
  );
}

function AdminEditor({ q, onSaved }) {
  const [f, setF] = useState(q);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const total = Math.max(Number(f.price || 0) + Number(f.extra_charges || 0) - Number(f.discount || 0), 0);
  const act = async (path, body) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/quotations/${q.id}`, f);
      let out = data;
      if (path) out = (await api.post(`/quotations/${q.id}/${path}`, body || {})).data;
      toast.success(path ? `Quotation ${path === "convert" ? "converted to order" : path === "send" ? "sent" : "rejected"}` : "Saved");
      onSaved(path === "convert" ? null : out);
    } catch (e) {
      toast.error(formatErr(e));
    }
    setBusy(false);
  };
  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-3">
        {EDIT.map(([k, l]) => <Field key={k} label={l}><Input value={f[k] || ""} onChange={set(k)} className={inputCls} data-testid={`qedit-${k}`} /></Field>)}
        <Field label="Requirements" full><Textarea value={f.requirements || ""} onChange={set("requirements")} className={inputCls} /></Field>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Field label="Price"><Input type="number" value={f.price} onChange={set("price")} className={inputCls} data-testid="qedit-price" /></Field>
        <Field label="Extra charges"><Input type="number" value={f.extra_charges} onChange={set("extra_charges")} className={inputCls} data-testid="qedit-extra" /></Field>
        <Field label="Discount"><Input type="number" value={f.discount} onChange={set("discount")} className={inputCls} data-testid="qedit-discount" /></Field>
        <Field label="Currency"><Input value={f.currency || "USD"} onChange={set("currency")} className={inputCls} /></Field>
        <Field label="Estimated completion" full><Input value={f.eta || ""} onChange={set("eta")} placeholder="e.g. 3-5 days" className={inputCls} data-testid="qedit-eta" /></Field>
        <div className="col-span-2 flex items-end"><p className="font-display text-3xl text-electric" data-testid="qedit-total">Total {total.toFixed(2)}</p></div>
        <Field label="Notes to customer" full><Textarea value={f.admin_notes || ""} onChange={set("admin_notes")} className={inputCls} data-testid="qedit-notes" /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3"><CustomAnswers items={q.custom} /></div>
      {q.customer_note && <p className="text-sm text-slate-300">Customer note: {q.customer_note}</p>}
      <div className="flex flex-wrap gap-2">
        <button disabled={busy} onClick={() => act()} className="btn-ghost !py-2" data-testid="qedit-save">Save</button>
        <button disabled={busy} onClick={() => act("send")} className="btn-electric !py-2" data-testid="qedit-send">{["sent", "declined"].includes(q.status) ? "Send revision" : "Approve & send"}</button>
        {q.status === "accepted" && !q.order_id && <button disabled={busy} onClick={() => act("convert")} className="btn-electric !py-2" data-testid="qedit-convert">Convert to order</button>}
        {!["accepted", "rejected"].includes(q.status) && <button disabled={busy} onClick={() => act("reject", { reason: f.admin_notes })} className="btn-ghost !py-2 !border-rose-500/50 !text-rose-300" data-testid="qedit-reject">Reject</button>}
      </div>
    </div>
  );
}

function CustomerRespond({ q, onSaved }) {
  const [note, setNote] = useState("");
  const respond = async (action) => {
    try {
      const { data } = await api.post(`/quotations/${q.id}/respond`, { action, note });
      toast.success(action === "accept" ? `Accepted! Order ${data.order?.number} created — complete payment in My Orders.` : "Quotation declined");
      onSaved(data.quotation);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  return (
    <div className="space-y-3">
      <Pricing q={q} />
      {q.status === "sent" && (
        <>
          <Textarea placeholder="Optional note for our team" value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} data-testid="quote-respond-note" />
          <div className="flex gap-2">
            <button onClick={() => respond("accept")} className="btn-electric !py-2" data-testid="quote-accept-btn">Accept quotation</button>
            <button onClick={() => respond("decline")} className="btn-ghost !py-2" data-testid="quote-decline-btn">Decline</button>
          </div>
        </>
      )}
    </div>
  );
}

export default function Quotations() {
  const { user } = useAuth();
  const admin = user.role === "admin";
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const load = useCallback(() => api.get("/quotations").then((r) => setItems(r.data)), []);
  useEffect(() => {
    load();
  }, [load]);
  useRealtimeEvent((m) => {
    if (m.event === "notification" && m.data.type === "quotation") load();
    if (m.event === "refresh") load();
  });
  const shown = items.filter((i) => (tab === "all" || i.status === tab) && (!q || `${i.number} ${i.name} ${i.email} ${i.service}`.toLowerCase().includes(q.toLowerCase())));
  const onSaved = (d) => {
    load();
    setSel(d);
  };
  return (
    <div data-testid="quotations-page">
      <PageHeader title={admin ? "Quotations" : "My Quotations"} desc={admin ? "Review, price and send quotations" : "Track and respond to your quotation requests"}>
        {!admin && <a href="/quote" className="btn-electric !py-2" data-testid="new-quote-btn">New request</a>}
      </PageHeader>
      <div className="flex flex-wrap gap-2 mb-4">
        {TABS.map((t) => <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 rounded-full text-sm capitalize border ${tab === t ? "bg-[#007bff] border-[#007bff] text-white" : "border-[#007bff]/30 text-slate-300"}`} data-testid={`quote-tab-${t}`}>{t} ({t === "all" ? items.length : items.filter((i) => i.status === t).length})</button>)}
        {admin && <div className="relative ml-auto w-full sm:w-64"><Search className="w-4 h-4 absolute left-3 top-3 text-slate-500" /><Input placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} className={`${inputCls} pl-9`} data-testid="quote-search" /></div>}
      </div>
      {shown.length === 0 ? <Empty text="No quotations here yet." /> : (
        <div className="grid gap-3">
          {shown.map((i) => (
            <button key={i.id} onClick={() => setSel(i)} className="card-neon card-neon-hover p-4 text-left flex flex-wrap items-center gap-3 justify-between" data-testid={`quote-row-${i.number}`}>
              <div className="min-w-0">
                <p className="font-semibold text-white">{i.number} · {i.service}{i.service_option ? ` — ${i.service_option}` : ""}</p>
                <p className="text-xs text-slate-400">{admin ? `${i.name} · ${i.email} · ` : ""}{i.platform} · {fmtDate(i.created_at)}</p>
              </div>
              <div className="flex items-center gap-3">{i.total > 0 && <span className="font-cond font-bold text-lg text-[#00d9ff]">{i.total} {i.currency}</span>}<StatusBadge s={i.status} testid={`quote-status-${i.number}`} /></div>
            </button>
          ))}
        </div>
      )}
      <Dialog open={!!sel} onOpenChange={() => setSel(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto scroll-thin bg-[#050a18] border-[#007bff]/40">
          {sel && (
            <>
              <DialogTitle className="font-display text-3xl text-metal flex items-center gap-3 flex-wrap">{sel.number} <StatusBadge s={sel.status} /></DialogTitle>
              {admin ? <AdminEditor key={sel.id + sel.updated_at} q={sel} onSaved={onSaved} /> : (<><Info q={sel} /><CustomerRespond q={sel} onSaved={onSaved} /></>)}
              {admin && <div className="border-t border-white/10 pt-4"><p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Submitted screenshots</p><Info q={{ screenshots: sel.screenshots }} /></div>}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
